import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { heroContent } from '../../data/mock';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';

const HeroSection = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const images = heroContent.backgroundImages;
  
  // Initialize with random index based on current route/time
  const getRandomIndex = useCallback(() => Math.floor(Math.random() * images.length), [images.length]);
  const [currentIndex, setCurrentIndex] = useState(() => getRandomIndex());

  // Randomize image when route changes (menu click)
  useEffect(() => {
    setCurrentIndex(getRandomIndex());
  }, [location.pathname, getRandomIndex]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  }, [images.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  // Auto-play carousel
  useEffect(() => {
    const interval = setInterval(() => {
      nextSlide();
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [nextSlide]);

  const headline = t('hero.headline');

  return (
    <section id="home" className="relative min-h-screen flex items-center">
      {/* Background Images with Carousel */}
      <div className="absolute inset-0">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={image}
              alt={`Fire protection ${index + 1}`}
              className="w-full h-full object-cover"
              loading={index === 0 ? "eager" : "lazy"}
              fetchPriority={index === 0 ? "high" : "low"}
              decoding={index === 0 ? "sync" : "async"}
            />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
      </div>

      {/* Carousel Indicators */}
      <div className="absolute top-28 left-1/2 transform -translate-x-1/2 flex space-x-2 z-10">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-colors duration-300 ${
              index === currentIndex
                ? 'bg-[#e21b1b]'
                : 'bg-gray-400/50 hover:bg-[#e21b1b]/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="max-w-2xl">
          <h1 className="font-montserrat font-black leading-tight mb-6 uppercase">
            <span className="block text-2xl sm:text-3xl lg:text-4xl mb-1 drop-shadow-lg text-[#e21b1b]">
              {headline[0]}
            </span>
            <span className="block text-2xl sm:text-3xl lg:text-4xl mb-1 drop-shadow-lg text-white">
              {headline[1]}
            </span>
            <span className="block text-2xl sm:text-3xl lg:text-4xl text-white drop-shadow-lg">
              {headline[2]}
            </span>
          </h1>
          <p className="font-roboto text-white/90 text-base sm:text-lg mb-8 max-w-md leading-relaxed">
            {t('hero.description')}
          </p>
          <a href="/contact#contact-form">
            <Button
              className="bg-transparent border-2 border-white text-white hover:bg-[#e21b1b] hover:border-[#e21b1b] font-roboto font-medium tracking-wider px-8 py-6 text-sm transition-all duration-300 uppercase"
            >
              {t('hero.cta')}
            </Button>
          </a>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/50 rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
